import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Chip,
  IconButton,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  Card,
  CardContent,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { ticketService } from '../../services/ticketService';
import { Ticket, UpdateTicketRequest } from '../../types/ticket';
import { profileService } from '../../services/profileService';
import { messageService } from '../../services/messageService';
import TrademarkFooter from '../common/TrademarkFooter';

const CRMTickets: React.FC = () => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<Ticket>>({});
  const [userFullName, setUserFullName] = useState('');
  const [stats, setStats] = useState<{ total: number; bySeverity: { [key: string]: number } }>({ total: 0, bySeverity: {} });
  const [smsCosts, setSmsCosts] = useState<{ [orgId: string]: number }>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ticketToDelete, setTicketToDelete] = useState<Ticket | null>(null);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState<string>('');
  const [selectedTicketEmail, setSelectedTicketEmail] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchTickets();
    fetchStats();
    fetchSMSCosts();
    const fetchUserProfile = async () => {
      const profile = await profileService.getCurrentUserProfile();
      if (profile) {
        setUserFullName(profile.full_name);
      }
    };
    fetchUserProfile();
  }, []);

  const fetchTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ticketService.getAllTickets();
      setTickets(data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
      setError('Failed to fetch tickets');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const data = await ticketService.getTicketStats();
      setStats(data);
    } catch (error) {
      console.error('Error fetching ticket stats:', error);
    }
  };

  const fetchSMSCosts = async () => {
    try {
      const data = await messageService.getSMSCostsByOrg();
      setSmsCosts(data);
    } catch (error) {
      console.error('Error fetching SMS costs:', error);
    }
  };

  const handleEdit = (ticket: Ticket) => {
    setEditingId(ticket.id);
    setEditFormData(ticket);
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditFormData({});
  };

  const handleSave = async () => {
    if (editingId) {
      try {
        setError(null);
        const updateData: UpdateTicketRequest = {};
        if (editFormData.email !== undefined) updateData.email = editFormData.email;
        if (editFormData.severity !== undefined) updateData.severity = editFormData.severity;
        if (editFormData.message !== undefined) updateData.message = editFormData.message;

        await ticketService.updateTicket(editingId, updateData);
        await fetchTickets(); // Refresh the list after updating
        await fetchStats(); // Refresh stats
        setEditingId(null);
        setEditFormData({});
      } catch (error) {
        console.error('Error updating ticket:', error);
        setError('Failed to update ticket');
      }
    }
  };

  const handleEditChange = (field: keyof Ticket) => (
    event: React.ChangeEvent<HTMLInputElement> | { target: { value: unknown } }
  ) => {
    setEditFormData({
      ...editFormData,
      [field]: event.target.value,
    });
  };

  const handleDeleteClick = (ticket: Ticket) => {
    setTicketToDelete(ticket);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTicketToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (ticketToDelete) {
      try {
        setError(null);
        await ticketService.deleteTicket(ticketToDelete.id);
        await fetchTickets(); // Refresh the list after deleting
        await fetchStats(); // Refresh stats
        setDeleteDialogOpen(false);
        setTicketToDelete(null);
      } catch (error) {
        console.error('Error deleting ticket:', error);
        setError('Failed to delete ticket');
      }
    }
  };

  const handleViewMessage = (message: string, email: string) => {
    setSelectedMessage(message);
    setSelectedTicketEmail(email);
    setMessageDialogOpen(true);
  };

  const handleCloseMessageDialog = () => {
    setMessageDialogOpen(false);
    setSelectedMessage('');
    setSelectedTicketEmail('');
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      case 'low':
        return 'success';
      case 'suggestion':
        return 'default';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const renderEditableCell = (ticket: Ticket, field: keyof Ticket) => {
    const isEditing = editingId === ticket.id;
    const value = isEditing ? editFormData[field] || ticket[field] : ticket[field];

    if (isEditing) {
      if (field === 'severity') {
        return (
          <FormControl size="small" fullWidth>
            <Select
              value={value as string}
              onChange={handleEditChange(field)}
              displayEmpty
            >
              <MenuItem value="low">Low</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
              <MenuItem value="suggestion">Suggestion</MenuItem>
            </Select>
          </FormControl>
        );
      } else if (field === 'message') {
        return (
          <TextField
            multiline
            rows={2}
            value={value as string}
            onChange={handleEditChange(field)}
            size="small"
            fullWidth
          />
        );
      } else {
        return (
          <TextField
            value={value as string}
            onChange={handleEditChange(field)}
            size="small"
            fullWidth
          />
        );
      }
    }

    if (field === 'severity') {
      return (
        <Chip
          label={value as string}
          color={getSeverityColor(value as string)}
          size="small"
          variant="outlined"
        />
      );
    }

    if (field === 'message') {
      const message = value as string;
      const truncatedMessage = message.length > 100 ? `${message.substring(0, 100)}...` : message;
      
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" sx={{ maxWidth: 300, wordBreak: 'break-word' }}>
            {truncatedMessage}
          </Typography>
          {message.length > 100 && (
            <Button
              size="small"
              variant="text"
              onClick={() => handleViewMessage(message, ticket.email)}
              sx={{ minWidth: 'auto', p: 0.5 }}
            >
              View Full
            </Button>
          )}
        </Box>
      );
    }

    return <Typography variant="body2">{value as string}</Typography>;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          CRM Tickets Dashboard
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Welcome back, {userFullName}! Manage and respond to support tickets.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Statistics Cards */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Total Tickets
            </Typography>
            <Typography variant="h4" component="div">
              {stats.total}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Critical
            </Typography>
            <Typography variant="h4" component="div" color="error.main">
              {stats.bySeverity.critical || 0}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              High Priority
            </Typography>
            <Typography variant="h4" component="div" color="warning.main">
              {stats.bySeverity.high || 0}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Typography color="text.secondary" gutterBottom>
              Suggestions
            </Typography>
            <Typography variant="h4" component="div" color="info.main">
              {stats.bySeverity.suggestion || 0}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ flex: '1 1 200px', minWidth: 200 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AttachMoneyIcon color="success" />
              <Box>
                <Typography color="text.secondary" gutterBottom>
                  SMS Costs
                </Typography>
                <Typography variant="h4" component="div" color="success.main">
                  ${Object.values(smsCosts).reduce((sum, cost) => sum + cost, 0).toFixed(2)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Actions */}
      <Box sx={{ mb: 3, display: 'flex', gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={fetchTickets}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      {/* Tickets Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        <TableContainer sx={{ maxHeight: 600 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Created At</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Message</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tickets.map((ticket) => (
                <TableRow key={ticket.id} hover>
                  <TableCell>
                    {ticket.created_at ? formatDate(ticket.created_at) : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {renderEditableCell(ticket, 'email')}
                  </TableCell>
                  <TableCell>
                    {renderEditableCell(ticket, 'severity')}
                  </TableCell>
                  <TableCell>
                    {renderEditableCell(ticket, 'message')}
                  </TableCell>
                  <TableCell>
                    {editingId === ticket.id ? (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Save">
                          <IconButton
                            size="small"
                            onClick={handleSave}
                            color="primary"
                          >
                            <SaveIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Cancel">
                          <IconButton
                            size="small"
                            onClick={handleCancel}
                            color="default"
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleEdit(ticket)}
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteClick(ticket)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this ticket? This action cannot be undone.
          </Typography>
          {ticketToDelete && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Email:</strong> {ticketToDelete.email}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Severity:</strong> {ticketToDelete.severity}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                <strong>Message:</strong> {ticketToDelete.message}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Message View Dialog */}
      <Dialog 
        open={messageDialogOpen} 
        onClose={handleCloseMessageDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Ticket Message
          {selectedTicketEmail && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              From: {selectedTicketEmail}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {selectedMessage}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseMessageDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      <TrademarkFooter />
    </Container>
  );
};

export default CRMTickets; 